import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  ShoppingOutlined, CloseCircleOutlined, EnvironmentOutlined,
  CreditCardOutlined, CheckCircleOutlined, LockOutlined, RightOutlined,
} from "@ant-design/icons";
import { Form, Typography, Radio, Modal, Spin, Image, Space } from "antd";

import FormInput from "../../components/FormInput/FormInput";
import { selectUserFromUserState } from "../../redux-toolkit/user/user-selector";
import { selectCartItems, selectTotalPrice } from "../../redux-toolkit/cart/cart-selector";
import { resetOrderState, setOrderLoadingState } from "../../redux-toolkit/order/order-slice";
import { LoadingStatus } from "../../types/types";
import { resetCartState } from "../../redux-toolkit/cart/cart-slice";
import { fetchCart } from "../../redux-toolkit/cart/cart-thunks";
import OrderItem from "./OrderItem/OrderItem";
import { BASE_URL } from "../../constants/urlConstants";
import "./Order.css";

type PaymentType    = "COD" | "ONLINE";
type DeliveryStatus = "AVAILABLE" | "NOT_AVAILABLE" | null;

const GST_RATE = 0.05;   // 5% total GST (CGST 2.5% + SGST 2.5%)

const HQ_EMAIL  = process.env.REACT_APP_HQ_EMAIL ?? "finllietech@gmail.com";
const HQ_MOBILE = process.env.REACT_APP_HQ_MOBILE ?? "";
const HQ_UPI_ID = process.env.REACT_APP_HQ_UPI_ID ?? "";

const buildUpiUrl = (upiId: string, payeeName: string, amount: number) =>
  `upi://pay?pa=${encodeURIComponent(upiId)}` +
  `&pn=${encodeURIComponent(payeeName)}` +
  `&am=${encodeURIComponent(amount.toFixed(2))}` +
  `&cu=INR`;

const buildQrImageUrl = (data: string) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(data)}`;

const showErrorModal = (msg: string) =>
  Modal.error({ title: "Error", content: msg, okText: "OK", centered: true });

const safeFetch = async (
  url: string,
  options?: RequestInit
): Promise<{ ok: boolean; status: number; data: any }> => {
  const res  = await fetch(url, options);
  const text = await res.text();
  let data: any = text;
  try { if (text) data = JSON.parse(text); } catch { }
  return { ok: res.ok, status: res.status, data };
};

// ── GST helpers ──────────────────────────────────────────────────────────────
// MRP is inclusive of GST. Back-calculate base price and tax amounts.
const calcGST = (mrpTotal: number) => {
  const basePrice = +(mrpTotal / (1 + GST_RATE)).toFixed(2);
  const gstTotal  = +(mrpTotal - basePrice).toFixed(2);
  const cgst      = +(gstTotal / 2).toFixed(2);
  const sgst      = +(gstTotal / 2).toFixed(2);
  return { basePrice, gstTotal, cgst, sgst };
};

const Order: FC = (): ReactElement => {
  const dispatch = useDispatch();
  const history  = useHistory();
  const [form]   = Form.useForm();

  const [orderPlaced,    setOrderPlaced]    = useState(false);
  const [orderId,        setOrderId]        = useState<number | null>(null);
  const [orderError,     setOrderError]     = useState<string | null>(null);
  const [paymentType,    setPaymentType]    = useState<PaymentType>("COD");
  const [deliveryStatus, setDeliveryStatus] = useState<DeliveryStatus>(null);
  const [progress,       setProgress]       = useState({ open: false, text: "" });
  const [minOrderValue,  setMinOrderValue]  = useState<number>(0);
  const [perfumesFromLocalStorage, setPerfumesFromLocalStorage] =
    useState<Map<number, number>>(new Map());

  const [payeeUpiId, setPayeeUpiId] = useState("");
  const [payeeName, setPayeeName] = useState("");
  const [payeeEmail, setPayeeEmail] = useState("");
  const [payeeMobile, setPayeeMobile] = useState("");
  const [upiModalOpen, setUpiModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [upiUnavailableModalOpen, setUpiUnavailableModalOpen] = useState(false);

  const usersData  = useSelector(selectUserFromUserState);
  const perfumes   = useSelector(selectCartItems);
  const totalPrice = useSelector(selectTotalPrice); // already distributor-aware after cart-slice fix

  const role          = localStorage.getItem("role");
  const isDistributor = role === "3";
  const isRetailer    = role === "2";

  // ── GST breakdown (MRP inclusive) ──
  const { basePrice, cgst, sgst } = calcGST(totalPrice);

  // ── Min order fetch ──
  useEffect(() => {
    const fetchMinOrder = async () => {
      try {
        const endpoint = isDistributor
          ? `${BASE_URL}/api/v1/config/dist_max`
          : `${BASE_URL}/api/v1/config/retailer_max`;
        const { ok, data } = await safeFetch(endpoint);
        if (ok) setMinOrderValue(Number(data));
      } catch { }
    };
    fetchMinOrder();
  }, [isRetailer, isDistributor]);

  // ── Payee resolution: Retailer → their Distributor's UPI · Distributor → HQ's UPI ──
  useEffect(() => {
    const loadPayee = async () => {
      try {
        if (isDistributor) {
          setPayeeUpiId(HQ_UPI_ID);
          setPayeeName("Finllie HQ");
          setPayeeEmail(HQ_EMAIL);
          setPayeeMobile(HQ_MOBILE);
          return;
        }

        const distributorId = localStorage.getItem("distId");
        if (!distributorId) return;

        const res = await fetch(`${BASE_URL}/api/finalle/dist/code/${distributorId}`);
        if (!res.ok) return;

        const data = await res.json();
        setPayeeUpiId(data.upiId || "");
        setPayeeName(data.businessName || "Distributor");
        setPayeeEmail(data.email || "");
        setPayeeMobile(data.mobile || "");
      } catch { }
    };

    loadPayee();
  }, [isDistributor]);

  // ── Cart init ──
  useEffect(() => {
    const stored = new Map<number, number>(
      JSON.parse(localStorage.getItem("perfumes") || "[]")
    );
    setPerfumesFromLocalStorage(stored);
    dispatch(setOrderLoadingState(LoadingStatus.LOADED));
    dispatch(fetchCart(Array.from(stored.keys())));
    if (usersData) form.setFieldsValue(usersData);
    fetchPrimaryAddress();
    return () => { dispatch(resetOrderState()); dispatch(resetCartState()); };
  }, [dispatch, form, usersData]);

  // ── Auto-fetch pincode when pre-filled from address screen ──
  // Runs once after form is populated (slight delay to let setFieldsValue settle)
  useEffect(() => {
    const timer = setTimeout(() => {
      const pincode = form.getFieldValue("postIndex");
      if (pincode && /^\d{6}$/.test(pincode)) {
        fetchPincodeDetails(pincode);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, []);  // runs once on mount — form pre-fill happens in fetchPrimaryAddress above

  const fetchPrimaryAddress = async () => {
    const msisdn = localStorage.getItem("mobile");
    if (!msisdn) return;
    try {
      const { ok, data } = await safeFetch(`${BASE_URL}/api/address/get`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ msisdn }),
      });
      if (!ok || !Array.isArray(data) || !data.length) return;
      const addr = data.find((a: any) => a.primary) ?? data[0];
      form.setFieldsValue({
        address:   addr.address  ?? "",
        city:      addr.city     ?? "",
        state:     addr.state    ?? "",
        postIndex: addr.pincode  ?? "",
      });
      setDeliveryStatus("AVAILABLE");

      // ── Auto-fetch pincode details after address is populated ──
      if (addr.pincode && /^\d{6}$/.test(addr.pincode)) {
        await fetchPincodeDetails(addr.pincode);
      }
    } catch { }
  };

  const fetchPincodeDetails = async (pincode: string) => {
    if (!/^\d{6}$/.test(pincode)) return;

    const existingCity  = form.getFieldValue("city");
    const existingState = form.getFieldValue("state");
    if (!existingCity || !existingState) {
      form.setFieldsValue({ city: "", state: "" });
    }
    setDeliveryStatus(null);

    try {
      const response = await fetch(`${BASE_URL}/api/v1/order/pincode/${pincode}`);
      const data = await response.json();

      if (response.ok && data) {
        form.setFieldsValue({ city: data.city, state: data.state });
        setDeliveryStatus(data.deliveryAvailable ? "AVAILABLE" : "NOT_AVAILABLE");
        return;
      }

    } catch (err) {
      console.error("Primary pincode API failed:", err);
    }

    // Fallback to postal API
    try {
      const res = await fetch(`${BASE_URL}/api/v1/order/pincode/postal/${pincode}`);
      const data = await res.json();

      if (!res.ok || !data?.[0]?.PostOffice?.length) {
        setDeliveryStatus("NOT_AVAILABLE");
        return;
      }
      const po = data[0].PostOffice[0];
      form.setFieldsValue({ city: po.District, state: po.State });
      setDeliveryStatus(po.State === "Karnataka" ? "AVAILABLE" : "NOT_AVAILABLE");

    } catch {
      // ✅ Only show modal if truly unexpected error
      showErrorModal("Failed to fetch pincode details.");
      setDeliveryStatus("NOT_AVAILABLE");
    }
  };

  /* ================= PLACE ORDER ================= */
  const placeOrder = async (paymentMode: PaymentType, paymentNote?: string) => {
    const perfumesId = Object.fromEntries(
      new Map(JSON.parse(localStorage.getItem("perfumes") || "[]"))
    );
    const distributorId = localStorage.getItem("distributorId");
    const url  = isDistributor ? `${BASE_URL}/api/finalle/dist/order` : `${BASE_URL}/api/v1/order`;
    const body = isDistributor
      ? { distributorId: Number(distributorId), ...form.getFieldsValue(), perfumesId, paymentType: paymentMode, paymentNote }
      : { ...form.getFieldsValue(), perfumesId, totalPrice, paymentType: paymentMode, paymentNote };

    const { ok, data } = await safeFetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!ok) {
      const msg =
        typeof data === "object" && data?.message ? data.message :
        typeof data === "string" && data.trim()   ? data :
        "Order failed. Please try again.";
      throw new Error(msg);
    }

    localStorage.removeItem("perfumes");
    dispatch(resetCartState());
    setOrderId(data?.id ?? null);
    setOrderPlaced(true);
  };

  const isFormValid = () => {
    const v = form.getFieldsValue();
    return !!(v.address && v.postIndex && v.city && v.state && deliveryStatus === "AVAILABLE");
  };

  /* ================= SUBMIT ================= */
  const onFormSubmit = async () => {
    setOrderError(null);
    try {
      if (minOrderValue > 0 && totalPrice < minOrderValue)
        throw new Error(`Minimum order value is ₹${minOrderValue}`);

      setProgress({ open: true, text: "Checking your order..." });

      const perfumesId = Object.fromEntries(
        new Map(JSON.parse(localStorage.getItem("perfumes") || "[]"))
      );
      const msisdn = localStorage.getItem("mobile");

      let validOk = true;
      let validData: { valid: boolean; error?: string } = { valid: true };

      if (!isDistributor) {
        const response = await safeFetch(`${BASE_URL}/api/v1/order/validate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ phoneNumber: msisdn, perfumesId }),
        });
        validOk = response.ok;
        validData = response.data as { valid: boolean; error?: string };
      }

      if (!validOk || !validData?.valid) {
        throw new Error(validData?.error ?? "Some items are unavailable.");
      }

      if (paymentType === "COD") {
        setProgress({ open: true, text: "Processing your order..." });
        await placeOrder("COD");
        return;
      }

      setProgress({ open: false, text: "" });

      // ── ONLINE but no UPI ID set up → suggest COD instead of blocking ──
      if (!payeeUpiId) {
        setUpiUnavailableModalOpen(true);
        return;
      }

      // ── ONLINE → show UPI modal ──
      setUpiModalOpen(true);

    } catch (err: any) {
      setOrderError(err.message ?? "Something went wrong.");
    } finally {
      if (paymentType !== "ONLINE") setProgress({ open: false, text: "" });
    }
  };

  /* ================= UPI INTENT LAUNCH ================= */
  const launchUpiPayment = () => {
    if (!payeeUpiId) {
      setOrderError("UPI ID not configured.");
      return;
    }

    const upiUrl = buildUpiUrl(payeeUpiId, payeeName, totalPrice);

    // We can't detect success/failure of a UPI intent reliably,
    // so once the user returns to this tab/app, ask them to confirm.
    window.location.href = upiUrl;

    setTimeout(() => {
      setUpiModalOpen(false);
      setConfirmModalOpen(true);
    }, 4000);
  };

  /* ================= UPI UNAVAILABLE → FALL BACK TO COD ================= */
  const handleSwitchToCod = async () => {
    try {
      setUpiUnavailableModalOpen(false);
      setPaymentType("COD");
      setProgress({ open: true, text: "Placing your order..." });
      await placeOrder(
        "COD",
        isDistributor
          ? "UPI not available for HQ. Switched to COD."
          : `UPI not set up for ${payeeName || "this distributor"}. Switched to COD.`
      );
    } catch (err: any) {
      setOrderError(err.message);
    } finally {
      setProgress({ open: false, text: "" });
    }
  };

  /* ================= CONFIRM PAYMENT HANDLERS ================= */
  const handlePaymentConfirmed = async () => {
    try {
      setConfirmModalOpen(false);
      setProgress({ open: true, text: "Placing your order..." });
      await placeOrder(
        "ONLINE",
        `USER_CONFIRMED_UPI_PAYMENT | ${payeeName} | ${payeeUpiId}`
      );
    } catch (err: any) {
      setOrderError(err.message);
    } finally {
      setProgress({ open: false, text: "" });
    }
  };

  const handlePaymentNotDone = async () => {
    try {
      setConfirmModalOpen(false);
      setProgress({ open: true, text: "Placing your order..." });
      await placeOrder(
        "COD",
        "USER_DID_NOT_PAY_UPI | Collect during delivery"
      );
    } catch (err: any) {
      setOrderError(err.message);
    } finally {
      setProgress({ open: false, text: "" });
    }
  };

  const minOrderFail = minOrderValue > 0 && totalPrice < minOrderValue;

  if (orderPlaced) return (
    <div className="ord-success">
      <div className="ord-success-icon"><CheckCircleOutlined /></div>
      <h2 className="ord-success-title">Order Placed Successfully!</h2>
      <p className="ord-success-sub">Order #{orderId} · You'll receive a confirmation shortly</p>
      <button className="ord-success-btn" onClick={() => history.push("/")}>Continue Shopping</button>
    </div>
  );

  if (orderError) return (
    <div className="ord-success">
      <div className="ord-success-icon error"><CloseCircleOutlined /></div>
      <h2 className="ord-success-title">Order Failed</h2>
      <p className="ord-success-sub">{orderError}</p>
      <button className="ord-success-btn" onClick={() => setOrderError(null)}>Try Again</button>
    </div>
  );

  return (
    <>
      <Modal open={progress.open} footer={null} closable={false} centered>
        <div className="ord-progress-inner">
          <Spin size="large" />
          <Typography.Text className="ord-progress-text">{progress.text}</Typography.Text>
        </div>
      </Modal>

      {/* ── UPI PAYMENT MODAL ── */}
      <Modal
        title="UPI Payment"
        open={upiModalOpen}
        footer={null}
        onCancel={() => setUpiModalOpen(false)}
        centered
      >
        <Space direction="vertical" align="center" style={{ width: "100%" }}>

          <Typography.Text>
            Pay ₹{totalPrice.toFixed(2)} to <b>{payeeName}</b>
          </Typography.Text>

          {payeeUpiId && (
            <Image
              width={220}
              preview={false}
              src={buildQrImageUrl(buildUpiUrl(payeeUpiId, payeeName, totalPrice))}
              alt="UPI QR Code"
            />
          )}

          <Typography.Text copyable>
            {payeeUpiId}
          </Typography.Text>

          <button
            className="ord-confirm-btn"
            style={{ width: "100%" }}
            onClick={launchUpiPayment}
          >
            Open Google Pay / UPI App
          </button>

          <Typography.Text type="secondary" style={{ textAlign: "center" }}>
            After completing the payment, come back here and confirm.
          </Typography.Text>
        </Space>
      </Modal>

      {/* ── UPI UNAVAILABLE → SUGGEST COD ── */}
      <Modal
        title="Online Payment Unavailable"
        open={upiUnavailableModalOpen}
        closable={false}
        centered
        footer={[
          <button key="cancel" className="ad-cancel-btn" onClick={() => setUpiUnavailableModalOpen(false)}>
            Cancel
          </button>,
          <button key="cod" className="ord-confirm-btn" onClick={handleSwitchToCod}>
            Continue with COD
          </button>,
        ]}
      >
        <Typography.Paragraph>
          {isDistributor
            ? "HQ's UPI ID hasn't been set up yet, so online payment isn't available right now."
            : `${payeeName || "Your distributor"}'s UPI ID hasn't been set up yet, so online payment isn't available right now.`}
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary">
          You can place this order with Cash on Delivery instead and pay when it arrives.
        </Typography.Paragraph>
      </Modal>

      {/* ── PAYMENT CONFIRMATION MODAL ── */}
      <Modal
        title="Payment Confirmation"
        open={confirmModalOpen}
        closable={false}
        centered
        footer={[
          <button key="no" className="ad-cancel-btn" onClick={handlePaymentNotDone}>
            No, Not Paid
          </button>,
          <button key="yes" className="ord-confirm-btn" onClick={handlePaymentConfirmed}>
            Yes, Payment Done
          </button>,
        ]}
      >
        <Typography.Paragraph>
          Have you completed the UPI payment?
        </Typography.Paragraph>

        <Typography.Paragraph>
          If payment is completed, share the screenshot with:
          <br />
          {isDistributor ? (
            <>Admin (HQ)</>
          ) : (
            <>Distributor: <b>{payeeName}</b></>
          )}
          <br />
          {payeeEmail && <>Email: <b>{payeeEmail}</b><br /></>}
          {payeeMobile && <>Phone: <b>{payeeMobile}</b></>}
        </Typography.Paragraph>

        <Typography.Paragraph type="secondary">
          If payment was not completed, select "No, Not Paid".
          Payment can be collected during delivery.
        </Typography.Paragraph>
      </Modal>

      <div className="ord-page">
        <div className="ord-page-header">
          <ShoppingOutlined className="ord-page-header-icon" />
          <span className="ord-page-header-title">Checkout</span>
        </div>

        <div className="ord-layout">
          <div className="ord-left">

            {/* ── Delivery Address ── */}
            <div className="ord-card">
              <div className="ord-card-header">
                <EnvironmentOutlined className="ord-card-icon" />
                <span className="ord-card-title">Delivery Address</span>
                <button className="ord-manage-addr-btn" onClick={() => history.push("/account/user/myaddress")}>
                  Manage Addresses <RightOutlined style={{ fontSize: 11 }} />
                </button>
              </div>

              <Form
                form={form}
                layout="vertical"
                onValuesChange={(changed) => {
                  if (changed.postIndex?.length === 6) {
                    fetchPincodeDetails(changed.postIndex);
                  }
                }}
              >
                {!isDistributor && (
                  <>
                    <div className="ord-form-row">
                      <FormInput title="First Name" name="firstName" titleSpan={8} wrapperSpan={16} />
                      <FormInput title="Last Name"  name="lastName"  titleSpan={8} wrapperSpan={16} />
                    </div>
                    <div className="ord-form-row">
                      <FormInput title="Phone" name="phoneNumber" titleSpan={8} wrapperSpan={16} />
                      <FormInput title="Email" name="email"       titleSpan={8} wrapperSpan={16} />
                    </div>
                  </>
                )}

                <FormInput title="Address" name="address" titleSpan={8} wrapperSpan={16} />
                <div className="ord-form-row">
                  <FormInput title="Pincode" name="postIndex" titleSpan={8} wrapperSpan={16} />
                  <div className="ord-delivery-badge-wrap">
                    {deliveryStatus === "AVAILABLE" && (
                      <span className="ord-delivery-badge available">✓ Delivery available</span>
                    )}
                    {deliveryStatus === "NOT_AVAILABLE" && (
                      <span className="ord-delivery-badge unavailable">✗ Not serviceable</span>
                    )}
                  </div>
                </div>
                <div className="ord-form-row">
                  <FormInput title="City"  name="city"  disabled titleSpan={8} wrapperSpan={16} />
                  <FormInput title="State" name="state" disabled titleSpan={8} wrapperSpan={16} />
                </div>
              </Form>
            </div>

            {/* ── Payment Method ── */}
            <div className="ord-card">
              <div className="ord-card-header">
                <CreditCardOutlined className="ord-card-icon" />
                <span className="ord-card-title">Payment Method</span>
              </div>
              <div className="ord-payment-options">
                <label className={`ord-payment-option ${paymentType === "COD" ? "selected" : ""}`}>
                  <Radio checked={paymentType === "COD"} onChange={() => setPaymentType("COD")} />
                  <div className="ord-payment-info">
                    <span className="ord-payment-name">Cash on Delivery</span>
                    <span className="ord-payment-sub">Pay when your order arrives</span>
                  </div>
                </label>
                <label className={`ord-payment-option ${paymentType === "ONLINE" ? "selected" : ""}`}>
                  <Radio checked={paymentType === "ONLINE"} onChange={() => setPaymentType("ONLINE")} />
                  <div className="ord-payment-info">
                    <span className="ord-payment-name">
                      <LockOutlined style={{ fontSize: 11, marginRight: 4 }} />
                      Online Payment
                    </span>
                    <span className="ord-payment-sub">
                      {payeeUpiId
                        ? "Pay directly using UPI / Google Pay"
                        : "Not available yet — UPI ID not set up"}
                    </span>
                  </div>
                </label>
              </div>
              {minOrderFail && (
                <div className="ord-min-order-warn">
                  Minimum order value is ₹{minOrderValue}
                  {isRetailer    ? " (Retailer)"    : ""}
                  {isDistributor ? " (Distributor)" : ""}
                </div>
              )}
            </div>
          </div>

          {/* ── Order Summary ── */}
          <div className="ord-right">
            <div className="ord-card ord-summary-card">
              <div className="ord-card-header">
                <ShoppingOutlined className="ord-card-icon" />
                <span className="ord-card-title">Order Summary</span>
                <span className="ord-item-count">
                  {perfumes.length} item{perfumes.length !== 1 ? "s" : ""}
                </span>
              </div>

              <div className="ord-items-list">
                {perfumes.map((p) => (
                  <OrderItem key={p.id} perfume={p} quantity={perfumesFromLocalStorage.get(p.id)} />
                ))}
              </div>

              {/* ── Price Breakdown with GST ── */}
              <div className="ord-price-breakdown">

                {/* Base price (ex-GST) */}
                <div className="ord-price-row">
                  <span>Items total <span className="ord-gst-note">(excl. GST)</span></span>
                  <span>₹{basePrice.toFixed(2)}</span>
                </div>

                {/* GST split */}
                <div className="ord-price-row ord-gst-row">
                  <span>CGST <span className="ord-gst-note">(2.5%)</span></span>
                  <span>₹{cgst.toFixed(2)}</span>
                </div>
                <div className="ord-price-row ord-gst-row">
                  <span>SGST <span className="ord-gst-note">(2.5%)</span></span>
                  <span>₹{sgst.toFixed(2)}</span>
                </div>

                {/* Delivery */}
                <div className="ord-price-row">
                  <span>Delivery</span>
                  <span className="ord-free">FREE</span>
                </div>

                {/* Min order note */}
                {minOrderValue > 0 && (
                  <div className="ord-price-row ord-min-note">
                    <span>Min. order</span>
                    <span>₹{minOrderValue}</span>
                  </div>
                )}

                {/* Total (MRP inclusive of GST) */}
                <div className="ord-price-total">
                  <div>
                    <span>Total Amount</span>
                  </div>
                  <span>₹{totalPrice.toFixed(2)}</span>
                </div>
              </div>

              <button
                className="ord-confirm-btn"
                onClick={onFormSubmit}
                disabled={!isFormValid() || minOrderFail}
              >
                {paymentType === "ONLINE" ? `Proceed to Pay  ₹${totalPrice.toFixed(2)}` : "Place Order"}
              </button>
              <p className="ord-secure-note"><LockOutlined /> 100% Secure &amp; Encrypted</p>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Order;